import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

// Apply analytics consent to all third-party trackers (Clarity + GA / GTM).
// Called both on user click AND on mount to honour the persisted choice
// for returning visitors. Safe to call before the tag scripts finish loading:
// Clarity queues commands via window.clarity.q until the tag binds.
const applyConsent = (granted) => {
  if (typeof window === 'undefined') return;
  if (typeof window.clarity === 'function') {
    window.clarity('consent', granted);
  }
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: granted ? 'granted' : 'denied',
    });
  }
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Replay the persisted choice on every page load so returning visitors
    // keep their setting without seeing the banner again.
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent === 'accepted') {
      applyConsent(true);
    } else if (cookieConsent === 'declined') {
      applyConsent(false);
    } else {
      // First visit, no choice yet: show banner after a short delay.
      // Clarity is already default-denied by the snippet in index.html.
      setTimeout(() => {
        setIsVisible(true);
      }, 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    applyConsent(true);
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    applyConsent(false);
    setIsVisible(false);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-up">
      <div className="max-w-6xl mx-auto">
        <div className="bg-gray-900 border-2 border-red-500/30 rounded-lg shadow-2xl p-6 md:p-8">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            aria-label="Close cookie banner"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
                <Cookie className="w-6 h-6 text-red-500" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 pr-8">
              <h3 className="text-xl font-bold text-white mb-2">
                We Use Cookies
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies including analytics and performance tracking.
              </p>
              <Link
                to="/cookie-policy"
                className="text-red-500 hover:text-red-400 text-sm font-medium inline-flex items-center"
              >
                Learn more about our cookies →
              </Link>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Button
                onClick={handleDecline}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white whitespace-nowrap"
              >
                Decline
              </Button>
              <Button
                onClick={handleAccept}
                className="btn-primary whitespace-nowrap"
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
